import React, { useMemo } from 'react';
import {
  CalendarIcon,
  ChartBarIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import CollapsibleFilterSection from '../screener/filters/CollapsibleFilterSection';
import RangeSliderWithHistogram from '../screener/filters/RangeSliderWithHistogram';
import { DistributionBucket } from '../screener/filters/types';
import { PresetSelector } from '../presets';
import { usePresets } from '../../hooks/usePresets';
import { Preset, ContractFilters as PresetContractFilters } from '../../types/presets';
import InfoTooltip from '../common/InfoTooltip';
import '../screener/filters/FilterSidebar.css';
import './ContractFilterSidebar.css';

// Contract filter types
export interface ContractFilters {
  expirations?: string[]; // Selected expiration dates
  strike_min?: number;
  strike_max?: number;
  delta_min?: number;
  delta_max?: number;
  aroc_min?: number;
  aroc_max?: number;
  iv_min?: number;
  iv_max?: number;
  spread_max?: number;
  // Greeks filters
  open_interest_min?: number;
  theta_min?: number;
  theta_max?: number;
  gamma_min?: number;
  gamma_max?: number;
  vega_min?: number;
  vega_max?: number;
}

// Tooltip text for each filter (educational content for beginners)
const CONTRACT_FILTER_TOOLTIPS = {
  expirations: `Days until contract expires.

• Short (7-21 days): Faster decay, more trades
• Medium (30-45 days): Balanced risk/reward
• Long (60+ days): Slower decay, less management

Tip: Avoid expirations spanning earnings dates`,

  strike: `Price at which you may sell shares.

• ATM: Near current price
• OTM: Above current price (more upside)
• ITM: Below current price (more protection)

For covered calls, OTM strikes are typical`,

  delta: `Probability of expiring in-the-money.

• Conservative: 0.15-0.20 (~15-20% ITM chance)
• Moderate: 0.20-0.30 (~20-30% ITM chance)
• Aggressive: 0.30-0.40 (~30-40% ITM chance)

↑ Higher delta = more premium, more assignment risk
↓ Lower delta = less premium, keeps shares`,

  annReturn: `Projected yearly return if repeated.

• Conservative: 12-24%
• Moderate: 24-48%
• Aggressive: 48%+

↑ Higher = more return but more risk
↓ Lower = safer but less income`,

  iv: `Contract's implied volatility.

• Low: <30% (less premium)
• Normal: 30-50%
• High: >50% (more premium, more risk)

Compare to stock's IV Rank for context`,

  spread: `Gap between bid/ask prices.

• Excellent: <5%
• Acceptable: 5-15%
• Avoid: >15%

↑ Higher = harder to profit
↓ Lower = better execution`,

  openInterest: `Number of open contracts.

• Excellent: ≥10,000 (tight spreads <4%)
• Good: ≥5,000 (spreads 4-6%)
• Fair: ≥2,000 (spreads 5-8%)
• Low: ≥1,000 (spreads 8-15%)
• Poor: <1,000 (wide spreads >15%)

Note: Weekly expirations need ~2x higher OI
↑ Higher = better fills, tighter spreads`,

  theta: `Daily time decay ($/day).

• Theta is negative for option buyers
• As a CC seller, theta works for you
• Accelerates near expiration

More negative = faster decay = good for sellers`,

  gamma: `Rate of delta change.

• Higher gamma = delta changes quickly
• Lower gamma = more stable position
• Peaks near ATM and expiration

For CC: Lower gamma = more predictable`,

  vega: `Sensitivity to volatility changes.

• Higher vega = more IV exposure
• Lower vega = less IV risk

If IV drops after selling, you profit from vega`
};

interface ExpirationOption {
  date: string;
  display: string;
  days_to_expiration: number | null;
  count: number;
  /** From the backend (gold.v_option_chain): the exact third-Friday test, and earnings on or before the expiry. */
  expiry_cycle?: 'monthly' | 'weekly' | null;
  earnings_inside?: boolean | null;
}

interface ContractFilterSidebarProps {
  filters: ContractFilters;
  onChange: (filters: ContractFilters) => void;
  resultCount: number;
  isLoadingCount?: boolean;
  expirationOptions: ExpirationOption[];
  distributions?: {
    strike?: DistributionBucket[];
    delta?: DistributionBucket[];
    aroc?: DistributionBucket[];
    iv?: DistributionBucket[];
    spread?: DistributionBucket[];
    open_interest?: DistributionBucket[];
    theta?: DistributionBucket[];
    gamma?: DistributionBucket[];
    vega?: DistributionBucket[];
  };
  ranges?: {
    strike?: { min: number; max: number };
    aroc?: { min: number; max: number };
    iv?: { min: number; max: number };
    spread?: { min: number; max: number };
    open_interest?: { min: number; max: number };
    theta?: { min: number; max: number };
    gamma?: { min: number; max: number };
    vega?: { min: number; max: number };
  };
  // Preset support
  activePresetId?: number | null;
  onSelectPreset?: (presetId: number | null) => void;
  onSavePreset?: () => void;
  // Earnings date for warning
  nextEarningsDate?: string | null;
}

// Default empty distribution for loading state
const emptyDistribution: DistributionBucket[] = Array(10).fill({
  min: 0,
  max: 10,
  count: 0,
  percentage: 0
});

const ContractFilterSidebar: React.FC<ContractFilterSidebarProps> = ({
  filters,
  onChange,
  resultCount,
  isLoadingCount = false,
  expirationOptions,
  distributions,
  ranges,
  activePresetId,
  onSelectPreset,
  onSavePreset,
  nextEarningsDate
}) => {
  // Presets hook
  const { presets } = usePresets();
  // Use dynamic ranges with fallback to defaults
  const strikeMin = ranges?.strike?.min ?? 0;
  const strikeMax = ranges?.strike?.max ?? 1000;
  const arocMin = ranges?.aroc?.min ?? 0;
  const arocMax = ranges?.aroc?.max ?? 200;
  const ivMin = ranges?.iv?.min ?? 0;
  const ivMax = ranges?.iv?.max ?? 200;
  const spreadMin = ranges?.spread?.min ?? 0;
  const spreadMax = ranges?.spread?.max ?? 30;
  // Greeks filter ranges
  const oiMin = ranges?.open_interest?.min ?? 0;
  const oiMax = ranges?.open_interest?.max ?? 10000;
  const thetaMin = ranges?.theta?.min ?? -1;
  const thetaMax = ranges?.theta?.max ?? 0;
  const gammaMin = ranges?.gamma?.min ?? 0;
  const gammaMax = ranges?.gamma?.max ?? 0.1;
  const vegaMin = ranges?.vega?.min ?? 0;
  const vegaMax = ranges?.vega?.max ?? 1;

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.expirations && filters.expirations.length > 0) count++;
    if (filters.strike_min !== undefined && filters.strike_min > strikeMin) count++;
    if (filters.strike_max !== undefined && filters.strike_max < strikeMax) count++;
    if (filters.delta_min !== undefined && filters.delta_min > 0) count++;
    if (filters.delta_max !== undefined && filters.delta_max < 0.5) count++;
    if (filters.aroc_min !== undefined && filters.aroc_min > arocMin) count++;
    if (filters.aroc_max !== undefined && filters.aroc_max < arocMax) count++;
    if (filters.iv_min !== undefined && filters.iv_min > ivMin) count++;
    if (filters.iv_max !== undefined && filters.iv_max < ivMax) count++;
    if (filters.spread_max !== undefined && filters.spread_max < spreadMax) count++;
    if (filters.open_interest_min !== undefined && filters.open_interest_min > oiMin) count++;
    if (filters.theta_min !== undefined && filters.theta_min > thetaMin) count++;
    if (filters.theta_max !== undefined && filters.theta_max < thetaMax) count++;
    if (filters.gamma_min !== undefined && filters.gamma_min > gammaMin) count++;
    if (filters.gamma_max !== undefined && filters.gamma_max < gammaMax) count++;
    if (filters.vega_min !== undefined && filters.vega_min > vegaMin) count++;
    if (filters.vega_max !== undefined && filters.vega_max < vegaMax) count++;
    return count;
  }, [filters, strikeMin, strikeMax, arocMin, arocMax, ivMin, ivMax, spreadMax, oiMin, oiMax, thetaMin, thetaMax, gammaMin, gammaMax, vegaMin, vegaMax]);

  // Section-specific counts
  const expirationCount = useMemo(() => {
    return filters.expirations && filters.expirations.length > 0 ? 1 : 0;
  }, [filters.expirations]);

  const metricsCount = useMemo(() => {
    let count = 0;
    if (filters.strike_min !== undefined && filters.strike_min > strikeMin) count++;
    if (filters.strike_max !== undefined && filters.strike_max < strikeMax) count++;
    if (filters.delta_min !== undefined && filters.delta_min > 0) count++;
    if (filters.delta_max !== undefined && filters.delta_max < 0.5) count++;
    if (filters.aroc_min !== undefined && filters.aroc_min > arocMin) count++;
    if (filters.aroc_max !== undefined && filters.aroc_max < arocMax) count++;
    if (filters.iv_min !== undefined && filters.iv_min > ivMin) count++;
    if (filters.iv_max !== undefined && filters.iv_max < ivMax) count++;
    if (filters.spread_max !== undefined && filters.spread_max < spreadMax) count++;
    if (filters.open_interest_min !== undefined && filters.open_interest_min > oiMin) count++;
    if (filters.theta_min !== undefined && filters.theta_min > thetaMin) count++;
    if (filters.theta_max !== undefined && filters.theta_max < thetaMax) count++;
    if (filters.gamma_min !== undefined && filters.gamma_min > gammaMin) count++;
    if (filters.gamma_max !== undefined && filters.gamma_max < gammaMax) count++;
    if (filters.vega_min !== undefined && filters.vega_min > vegaMin) count++;
    if (filters.vega_max !== undefined && filters.vega_max < vegaMax) count++;
    return count;
  }, [filters, strikeMin, strikeMax, arocMin, arocMax, ivMin, ivMax, spreadMax, oiMin, oiMax, thetaMin, thetaMax, gammaMin, gammaMax, vegaMin, vegaMax]);

  // Reset all filters
  const handleResetAll = () => {
    onChange({});
    // Also clear preset when resetting
    if (onSelectPreset) {
      onSelectPreset(null);
    }
  };

  // Update a filter value
  const updateFilter = (updates: Partial<ContractFilters>) => {
    onChange({ ...filters, ...updates });
    // Clear active preset when filters are manually changed
    if (onSelectPreset && activePresetId) {
      onSelectPreset(null);
    }
  };

  // Handle preset selection
  const handleSelectPreset = (preset: Preset | null) => {
    if (preset) {
      // Apply preset's contract filters (keep expirations as they depend on stock)
      const newFilters: ContractFilters = {
        ...filters,
        expirations: filters.expirations, // Preserve current expirations
        delta_min: preset.contract_filters.delta_min,
        delta_max: preset.contract_filters.delta_max,
        aroc_min: preset.contract_filters.aroc_min,
        aroc_max: preset.contract_filters.aroc_max,
        iv_min: preset.contract_filters.contract_iv_min,
        iv_max: preset.contract_filters.contract_iv_max,
        spread_max: preset.contract_filters.contract_spread_max,
        open_interest_min: preset.contract_filters.open_interest_min,
        theta_min: preset.contract_filters.theta_min,
        theta_max: preset.contract_filters.theta_max,
        gamma_min: preset.contract_filters.gamma_min,
        gamma_max: preset.contract_filters.gamma_max,
        vega_min: preset.contract_filters.vega_min,
        vega_max: preset.contract_filters.vega_max,
      };
      onChange(newFilters);
      if (onSelectPreset) {
        onSelectPreset(preset.id);
      }
    } else {
      // Clear filters when "Custom" is selected (keep expirations)
      onChange({ expirations: filters.expirations });
      if (onSelectPreset) {
        onSelectPreset(null);
      }
    }
  };

  // Handle expiration toggle
  const handleExpirationToggle = (date: string) => {
    const currentExpirations = filters.expirations || [];
    const isSelected = currentExpirations.includes(date);

    if (isSelected) {
      updateFilter({
        expirations: currentExpirations.filter(d => d !== date)
      });
    } else {
      updateFilter({
        expirations: [...currentExpirations, date]
      });
    }
  };

  return (
    <div className="filter-sidebar">
      {/* Header */}
      <div className="filter-sidebar-header">
        <h3>
          Contract Filters
          {activeFilterCount > 0 && (
            <span className="filter-count-badge">{activeFilterCount}</span>
          )}
        </h3>
        {activeFilterCount > 0 && (
          <button
            className="filter-sidebar-reset"
            onClick={handleResetAll}
          >
            Reset All
          </button>
        )}
      </div>

      {/* Preset Selector */}
      <div className="filter-sidebar-presets">
        <PresetSelector
          activePresetId={activePresetId ?? null}
          onSelectPreset={handleSelectPreset}
          onSavePreset={onSavePreset}
          showSaveButton={!!onSavePreset}
        />
      </div>

      {/* Content */}
      <div className="filter-sidebar-content">
        {/* Section A: Expiration Dates */}
        <CollapsibleFilterSection
          title={
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              Expiration Dates
              <InfoTooltip text={CONTRACT_FILTER_TOOLTIPS.expirations} />
            </span>
          }
          icon={<CalendarIcon style={{ width: '18px', height: '18px' }} />}
          defaultExpanded={true}
          activeFilterCount={expirationCount}
        >
          <div className="contract-expiration-list">
            {expirationOptions.length === 0 ? (
              <div className="contract-expiration-empty">
                Loading expirations...
              </div>
            ) : (
              expirationOptions.map(exp => {
                const isAfterEarnings = !!exp.earnings_inside;
                const isMonthly = exp.expiry_cycle === 'monthly';
                return (
                  <label key={exp.date} className={`contract-expiration-item ${isAfterEarnings ? 'has-earnings-risk' : ''}`}>
                    <input
                      type="checkbox"
                      checked={(filters.expirations || []).includes(exp.date)}
                      onChange={() => handleExpirationToggle(exp.date)}
                      className="contract-expiration-checkbox"
                    />
                    <span className="contract-expiration-label">
                      {exp.display}
                      <span className="contract-expiration-days">
                        {exp.days_to_expiration != null ? `${exp.days_to_expiration}d` : '—'}
                      </span>
                      <span
                        className="contract-expiration-type-badge"
                        style={{
                          fontSize: '9px',
                          padding: '1px 4px',
                          borderRadius: '3px',
                          marginLeft: '4px',
                          fontWeight: 600,
                          backgroundColor: isMonthly ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.2)',
                          color: isMonthly ? '#10B981' : '#F59E0B'
                        }}
                      >
                        {isMonthly ? 'M' : 'W'}
                      </span>
                      {isAfterEarnings && (
                        <span className="contract-expiration-earnings-badge">
                          <ExclamationTriangleIcon style={{ width: '12px', height: '12px' }} />
                          Earnings
                        </span>
                      )}
                    </span>
                    <span className="contract-expiration-count">
                      {exp.count}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </CollapsibleFilterSection>

        {/* Section B: Contract Metrics */}
        <CollapsibleFilterSection
          title="Contract Metrics"
          icon={<ChartBarIcon style={{ width: '18px', height: '18px' }} />}
          defaultExpanded={true}
          activeFilterCount={metricsCount}
        >
          {/* Strike Price */}
          <RangeSliderWithHistogram
            label="Strike Price"
            min={strikeMin}
            max={strikeMax}
            step={1}
            value={[filters.strike_min ?? strikeMin, filters.strike_max ?? strikeMax]}
            onChange={([min, max]) => updateFilter({
              strike_min: min,
              strike_max: max
            })}
            distribution={distributions?.strike ?? emptyDistribution}
            formatValue={(v) => `$${v.toFixed(0)}`}
            unit=""
            tooltip={CONTRACT_FILTER_TOOLTIPS.strike}
          />

          {/* Delta */}
          <RangeSliderWithHistogram
            label="Delta"
            min={0}
            max={0.5}
            step={0.05}
            value={[filters.delta_min ?? 0, filters.delta_max ?? 0.5]}
            onChange={([min, max]) => updateFilter({
              delta_min: min,
              delta_max: max
            })}
            distribution={distributions?.delta ?? emptyDistribution}
            highlightRange={[0.15, 0.30]}
            highlightColor="#10B981"
            formatValue={(v) => v.toFixed(2)}
            unit=""
            tooltip={CONTRACT_FILTER_TOOLTIPS.delta}
          />

          {/* Annualized Return */}
          <RangeSliderWithHistogram
            label="Ann. Return"
            min={arocMin}
            max={arocMax}
            step={5}
            value={[filters.aroc_min ?? arocMin, filters.aroc_max ?? arocMax]}
            onChange={([min, max]) => updateFilter({
              aroc_min: min,
              aroc_max: max
            })}
            distribution={distributions?.aroc ?? emptyDistribution}
            highlightRange={[15, 50]}
            highlightColor="#10B981"
            unit="%"
            tooltip={CONTRACT_FILTER_TOOLTIPS.annReturn}
          />

          {/* Implied Volatility */}
          <RangeSliderWithHistogram
            label="IV"
            min={ivMin}
            max={ivMax}
            step={5}
            value={[filters.iv_min ?? ivMin, filters.iv_max ?? ivMax]}
            onChange={([min, max]) => updateFilter({
              iv_min: min,
              iv_max: max
            })}
            distribution={distributions?.iv ?? emptyDistribution}
            unit="%"
            tooltip={CONTRACT_FILTER_TOOLTIPS.iv}
          />

          {/* Spread */}
          <RangeSliderWithHistogram
            label="Bid/Ask Spread"
            min={spreadMin}
            max={spreadMax}
            step={1}
            value={[spreadMin, filters.spread_max ?? spreadMax]}
            onChange={([, max]) => updateFilter({
              spread_max: max
            })}
            distribution={distributions?.spread ?? emptyDistribution}
            highlightRange={[0, 10]}
            highlightColor="#10B981"
            unit="%"
            tooltip={CONTRACT_FILTER_TOOLTIPS.spread}
          />

          {/* Open Interest */}
          <RangeSliderWithHistogram
            label="Open Interest"
            min={oiMin}
            max={oiMax}
            step={100}
            value={[filters.open_interest_min ?? oiMin, oiMax]}
            onChange={([min]) => updateFilter({
              open_interest_min: min
            })}
            distribution={distributions?.open_interest ?? emptyDistribution}
            highlightRange={[500, oiMax]}
            highlightColor="#10B981"
            formatValue={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toString()}
            unit=""
            tooltip={CONTRACT_FILTER_TOOLTIPS.openInterest}
          />

          {/* Theta */}
          <RangeSliderWithHistogram
            label="Theta"
            min={thetaMin}
            max={thetaMax}
            step={0.01}
            value={[filters.theta_min ?? thetaMin, filters.theta_max ?? thetaMax]}
            onChange={([min, max]) => updateFilter({
              theta_min: min,
              theta_max: max
            })}
            distribution={distributions?.theta ?? emptyDistribution}
            formatValue={(v) => v.toFixed(3)}
            unit=""
            tooltip={CONTRACT_FILTER_TOOLTIPS.theta}
          />

          {/* Gamma */}
          <RangeSliderWithHistogram
            label="Gamma"
            min={gammaMin}
            max={gammaMax}
            step={0.005}
            value={[filters.gamma_min ?? gammaMin, filters.gamma_max ?? gammaMax]}
            onChange={([min, max]) => updateFilter({
              gamma_min: min,
              gamma_max: max
            })}
            distribution={distributions?.gamma ?? emptyDistribution}
            formatValue={(v) => v.toFixed(4)}
            unit=""
            tooltip={CONTRACT_FILTER_TOOLTIPS.gamma}
          />

          {/* Vega */}
          <RangeSliderWithHistogram
            label="Vega"
            min={vegaMin}
            max={vegaMax}
            step={0.01}
            value={[filters.vega_min ?? vegaMin, filters.vega_max ?? vegaMax]}
            onChange={([min, max]) => updateFilter({
              vega_min: min,
              vega_max: max
            })}
            distribution={distributions?.vega ?? emptyDistribution}
            formatValue={(v) => v.toFixed(3)}
            unit=""
            tooltip={CONTRACT_FILTER_TOOLTIPS.vega}
          />
        </CollapsibleFilterSection>
      </div>

      {/* Footer */}
      <div className="filter-sidebar-footer">
        <div className="filter-sidebar-count">
          {isLoadingCount ? (
            'Loading...'
          ) : (
            <>
              <strong>{resultCount.toLocaleString()}</strong> contracts match
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractFilterSidebar;
